import React, { Component } from 'react';
import classNames from 'classnames';

import ListViewItemPreviewAndControls from './list-view-item-preview-and-controls';
import ItemSize from './item-size';
import { clonePhotoWithoutEdits } from '../../utils/photo-utils';
import PhotoFilename from '../../common/photo-filename';
import findRecommendedPrintSize from '../../common/photo-quality/recommended-print-size';
import buildPhotoSizeBadges from '../../common/photo-quality/build-photo-size-badges';
import getPhotoPreviewWarningProps from '../../common/photo-quality/photo-preview-warning-props';

const baseNumberOfSizesToShow = 4;
const secondNumberOfSizesToShow = 10;
const subsequentPagesOfSizesToShow = 5;

const printSizeListEqual = (listA, listB) =>
  listA.length === listB.length &&
  listA.every(
    (o, i) =>
      Object.keys(o).length === Object.keys(listB[i]).length &&
      o.key === listB[i].key,
  );

class PhotosListItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      numberOfSizesToShow: baseNumberOfSizesToShow,
      recommendedPrintSize: findRecommendedPrintSize(
        props.sizes,
        this.rootPhoto,
      ),
    };
  }

  componentDidUpdate(prevProps) {
    const { sizes: previousSizes } = prevProps;
    const { sizes } = this.props;

    // If print sizes have changed then update recommendedPrintSize so that an invalid print size is not recommended or selectable
    if (!printSizeListEqual(previousSizes, sizes)) {
      const recommendedPrintSize = findRecommendedPrintSize(
        sizes,
        this.rootPhoto,
      );
      this.setState({
        recommendedPrintSize,
      });
    }
  }

  get rootPhoto() {
    const { photos, rootId } = this.props;

    // Match the photo with image_id matching the rootId prop or first photo
    const allPhotoInstances = Object.values(photos).flat();
    return (
      allPhotoInstances.find((p) => p.image_id === rootId) ||
      allPhotoInstances[0] ||
      null
    );
  }

  handleShowMoreSizesClick = () => {
    this.setState(({ numberOfSizesToShow: prevNumberOfSizesToShow }) => {
      const numberOfSizesToShow = (({ sizes }) => {
        if (prevNumberOfSizesToShow >= sizes.length) {
          return baseNumberOfSizesToShow;
        }

        if (prevNumberOfSizesToShow < secondNumberOfSizesToShow) {
          return secondNumberOfSizesToShow;
        }

        return prevNumberOfSizesToShow + subsequentPagesOfSizesToShow;
      })(this.props);

      return {
        numberOfSizesToShow,
      };
    });
  };

  rawDimensions() {
    const { photos } = this.props;

    const sizeWithRawDimensions = Object.keys(photos).find((sizeKey) => {
      const size = photos[sizeKey];
      return size && size[0] && size[0].raw_dimensions;
    });

    if (sizeWithRawDimensions) {
      return photos[sizeWithRawDimensions][0].raw_dimensions;
    }

    return null;
  }

  previews() {
    const { photos } = this.props;

    // Implement sorting for photo previews so that the "root" or original photo is first and
    // all cloned photos are display sequencially oldest to newest. New photos not yet saved will
    // not have an `image_id` until saved.
    const allPhotoInstances = Object.values(photos).flat();

    // 1. Find and pull out the root photo.
    // The root photo is the one whose image_id matches the rootId for the row.
    const rootPhoto = this.rootPhoto;

    // 2. Get all other photo instances for this image.
    const otherPhotos = allPhotoInstances.filter((p) => p !== rootPhoto);

    // 3. Sort the other photos: by ID ascending, with new (null ID) items last.
    otherPhotos.sort((a, b) => {
      if (a.id === b.id) return 0; // handles both nulls
      if (a.id === null) return 1;
      if (b.id === null) return -1;
      return a.id - b.id;
    });

    // 4. Re-combine, with the root photo always first.
    const sortedPreviews = [rootPhoto, ...otherPhotos];

    // 5. Filter out any null/undefined or zero-quantity items.
    return sortedPreviews.filter((p) => p && p.quantity > 0);
  }

  render() {
    const { sizes, photos, rootId, disabled } = this.props;
    const { numberOfSizesToShow, recommendedPrintSize } = this.state;

    const previews = this.previews();
    const moreFewerButtonToggleLabel = `${
      numberOfSizesToShow >= sizes.length ? 'Fewer' : 'More'
    } sizes`;

    // Sort size list so the recommended size is first, if there is one
    const sortedSizes = [...sizes].sort((a, b) => {
      if (!recommendedPrintSize) return 0;

      if (a.key === recommendedPrintSize.key) return -1;
      if (b.key === recommendedPrintSize.key) return 1;

      return 0;
    });

    const badgeTypeMap = buildPhotoSizeBadges(
      sortedSizes,
      previews,
      recommendedPrintSize,
    );

    let $previews = previews.map((photo, _key, array) => {
      const { showPreviewWarning, recommendedPrintSizeTextForPreview } =
        getPhotoPreviewWarningProps(photo, badgeTypeMap, recommendedPrintSize);

      return (
        <ListViewItemPreviewAndControls
          key={photo.client_uuid}
          photo={photo}
          isLastPhoto={array.length === 1}
          recommendedPrintSize={recommendedPrintSize}
          disabled={disabled}
          showSize
          showQuantity
          showPreviewWarning={showPreviewWarning}
          recommendedPrintSizeTextForPreview={
            recommendedPrintSizeTextForPreview
          }
        />
      );
    });

    if (previews.length === 0) {
      // previews.length becomes zero when the last photo is set to zero quantity,
      // when this happens the photo should be reset to the original. Rather than modify state
      // it strips edits before rendering the photo
      const photoWithoutEdits = clonePhotoWithoutEdits(this.rootPhoto);

      $previews = (
        <ListViewItemPreviewAndControls
          photo={{ ...photoWithoutEdits, quantity: 0 }}
          disabled={disabled}
          displayRootImageOnly
          showQuantity
        />
      );
    }

    return (
      <section className="list-item">
        <PhotoFilename photo={this.rootPhoto} />
        <div className="d-flex">
          <div className="flex-grow-1">{$previews}</div>
          <div
            className={classNames('list-item__sizes flex-shrink-0', {
              'list-item__sizes-expanded':
                numberOfSizesToShow > baseNumberOfSizesToShow,
            })}>
            <ul className="list-unstyled">
              {sortedSizes.slice(0, numberOfSizesToShow).map((size) => (
                <ItemSize
                  key={size.key}
                  rootId={rootId}
                  {...size}
                  sizeKey={size.key}
                  disabled={disabled}
                  badgeType={badgeTypeMap.get(size.key)}
                  photos={photos[size.key] || []}
                />
              ))}
            </ul>

            {sizes.length > baseNumberOfSizesToShow && (
              <button
                type="button"
                className="btn btn-link btn-underline list-item__sizes-toggle"
                aria-label={`View ${moreFewerButtonToggleLabel}`}
                onClick={this.handleShowMoreSizesClick}>
                {moreFewerButtonToggleLabel}
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }
}

export default PhotosListItem;
